import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { LayoutDashboard, ListChecks, Settings, LogOut, Activity, Bike, Webhook as WebhookIcon, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard",    icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/queue", label: "Cola",     icon: ListChecks,      testId: "nav-queue" },
  { to: "/webhooks", label: "Webhooks", icon: WebhookIcon,  testId: "nav-webhooks" },
  { to: "/partners", label: "Partners", icon: Users,         testId: "nav-partners" },
  { to: "/config", label: "Config",  icon: Settings,        testId: "nav-config" },
];

function HealthBadge() {
  const [health, setHealth] = useState(null);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/health");
        if (!stop) setHealth(data);
      } catch {
        if (!stop) setHealth({ status: "down" });
      }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(id); };
  }, []);
  const ok = health?.status === "ok";
  return (
    <div
      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full border bg-white text-xs font-mono"
      data-testid="health-indicator"
    >
      <Activity className={`w-3 h-3 ${ok ? "text-emerald-500" : "text-red-500"}`} />
      <span className="text-slate-700">healthcheck</span>
      <span className={`font-semibold ${ok ? "text-emerald-700" : "text-red-700"}`}>
        {ok ? "OK" : (health?.status || "...")}
      </span>
      {health?.adapter_mode && (
        <span className="text-slate-500">· modo <span className="text-slate-900 font-semibold">{health.adapter_mode}</span></span>
      )}
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#F9FAFB] text-slate-900" data-testid="admin-layout">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
              <div className="w-7 h-7 rounded-md bg-slate-900 flex items-center justify-center">
                <Bike className="w-4 h-4 text-white" />
              </div>
              <div className="leading-tight">
                <div className="text-sm font-bold tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>
                  hoomu · bridge
                </div>
                <div className="text-[10px] font-mono text-slate-500 -mt-0.5">evolvo ↔ insurancesoft</div>
              </div>
            </Link>
            <nav className="flex items-center gap-1">
              {NAV.map((n) => {
                const Icon = n.icon;
                const active = loc.pathname === n.to;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={n.testId}
                    className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-xs font-semibold transition ${
                      active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className="flex items-center gap-3">
            <HealthBadge />
            {user && (
              <div className="text-xs text-slate-600 hidden sm:block">
                <span className="font-mono">{user.username}</span>
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              data-testid="logout-button"
              className="h-8 text-xs"
              onClick={() => { logout(); nav("/login"); }}
            >
              <LogOut className="w-3.5 h-3.5 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 sm:px-8 py-8">{children}</main>
    </div>
  );
}
